import { inheritOutlookPrivacyMode } from '@/analytics/privacy/outlook-continuation-privacy'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { ViewerDocumentKind, ViewerOpenContextKind, type ContentViewerSession } from '@/content-viewer/store/schemas'
import { getContentViewerCapabilities } from '@/content-viewer/utils/get-content-viewer-capabilities'
import { selectActiveContentViewerSession } from '@/content-viewer/store/selectors'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { ContractAlignRight, FileDownload, OpenNew, OverflowMenu } from '@/assets/icons'
import { RootState } from '@/store/store'
import handleContentViewerDownload from '@/content-viewer/util/handle-content-viewer-download'
import { ReactNode, useState } from 'react'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import clsx from 'clsx'
import { twMerge } from 'tailwind-merge'
import { trackRiaSourceDownload, trackRiaSourceOpenInNewTabClicked, RiaSourceContext, createRiaContext } from '@/analytics/utils/ria-source-analytics'
import type { AnalyticsReference } from '@/analytics/schemas/analytics-reference.schemas'
import { RiaSourceTriggerLocation } from '@/analytics/enums/ria.enums'
import { selectAgentV2ConversationMatterId } from '@/agent-v2/conversations/store/selectors'
import { resolveStandaloneViewerUrlFromSession } from '@/standalone-reference-viewer/utils/resolve-standalone-viewer-url'
import { Link } from 'react-router-dom'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { IconButton } from '@/components/icon-button/IconButton'
import type { TooltipProps } from '@/components/ui/tooltip/Tooltip'
import TruncatedText from '@/components/truncated-text/TruncatedText'
import { kTooltipHoverDelayMs } from '@/constants/constants-ui'

export type ContentViewerHeaderVariant = 'default' | 'source-tab' | 'compact-source-tab'

export type ContentViewerHeaderProps = {
  title: string
  badge?: ReactNode
  variant?: ContentViewerHeaderVariant
}

/** Header title that shows its full text in a tooltip when it truncates. */
function ContentViewerHeaderTitle({ title, className, tooltipPlacement }: { title: string; className?: string; tooltipPlacement?: TooltipProps['placement'] }) {
  return (
    <TruncatedText placement={tooltipPlacement} className={twMerge('cursor-default text-base font-bold', className)}>
      {title}
    </TruncatedText>
  )
}

export default function ContentViewerHeader({ title, badge, variant = 'default' }: ContentViewerHeaderProps) {
  const dispatch = useAppDispatch()

  const { trackEvent } = useAnalytics()
  const [downloadLoading, setDownloadLoading] = useState(false)

  const contentViewerSession = useAppSelector(selectActiveContentViewerSession)
  const sessionCapabilities = getContentViewerCapabilities(contentViewerSession)
  const isDownloadSupported = sessionCapabilities.canDownload

  const sessionCitationContext = contentViewerSession?.context.kind === ViewerOpenContextKind.CHAT_CITATION ? contentViewerSession.context : null
  const sessionCitationMatterId = useAppSelector((state: RootState) => {
    if (!sessionCitationContext) return null
    const { conversationId: citationConversationId } = sessionCitationContext
    return selectAgentV2ConversationMatterId(state, { conversationId: citationConversationId })
  })
  const sessionRiaContext =
    sessionCitationContext && createRiaContext(sessionCitationContext.conversationId, sessionCitationContext.turnId, sessionCitationMatterId)
  const resolvedViewerUrl = resolveStandaloneViewerUrlFromSession(contentViewerSession, sessionRiaContext || undefined)
  const standaloneViewerUrl = resolvedViewerUrl ? inheritOutlookPrivacyMode(resolvedViewerUrl) : resolvedViewerUrl
  const showStandaloneViewerButton = sessionCapabilities.canOpenInNewTab && !!standaloneViewerUrl
  const isAgencySource = contentViewerSession?.document.kind === ViewerDocumentKind.AGENCY

  const handleClose = () => {
    // Clear the content viewer
    dispatch(ContentViewerActions.clearContentViewer())
  }

  const maybeTrackRiaSourceSessionDownload = (session: ContentViewerSession, context: RiaSourceContext | null | undefined) => {
    if (!context) return
    // RIA "source download" counts caselaw/LRR rich references only. User-file citations
    // are downloadable but are not RIA source downloads. Open-in-new-tab still
    // fires for all citation kinds via buildAnalyticsReferenceFromSession; keep this gate download-only.
    if (session.document.kind !== ViewerDocumentKind.CASELAW && session.document.kind !== ViewerDocumentKind.LRR) return
    const reference = buildAnalyticsReferenceFromSession(session)
    if (!reference) return

    trackRiaSourceDownload({
      trackEvent,
      context,
      reference,
      triggerLocation: RiaSourceTriggerLocation.ViewerHeaderButton,
    })
  }

  const handleDownload = async () => {
    if (!contentViewerSession) return

    maybeTrackRiaSourceSessionDownload(contentViewerSession, sessionRiaContext)
    setDownloadLoading(true)
    await handleContentViewerDownload(contentViewerSession)
    setDownloadLoading(false)
  }

  const handleOpenInNewTab = () => {
    if (!contentViewerSession) return
    const reference = buildAnalyticsReferenceFromSession(contentViewerSession)
    if (!reference || !sessionRiaContext) return

    trackRiaSourceOpenInNewTabClicked({
      trackEvent,
      context: sessionRiaContext,
      reference,
      triggerLocation: RiaSourceTriggerLocation.ContentViewerHeader,
    })
  }

  const openInNewTab = () => {
    if (!standaloneViewerUrl) return
    handleOpenInNewTab()
    window.open(standaloneViewerUrl, '_blank', 'noopener,noreferrer')
  }

  if (variant === 'source-tab' || variant === 'compact-source-tab') {
    const compact = variant === 'compact-source-tab'
    const busy = downloadLoading
    const hasActions = isDownloadSupported || showStandaloneViewerButton

    return (
      <div className={clsx('flex shrink-0 items-center gap-2 border-b bg-surface-content', compact ? 'min-h-[43px] px-3 py-1.5' : 'p-3')}>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <ContentViewerHeaderTitle title={title} className={compact ? 'text-sm font-semibold' : 'leading-6'} tooltipPlacement="bottom-start" />
          {badge && <div className="self-start">{badge}</div>}
        </div>
        {hasActions && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <IconButton
                variant="plain"
                color="neutral"
                size={compact ? 'sm' : 'md'}
                icon={<OverflowMenu />}
                loading={busy}
                aria-label="More actions"
                tooltipText="More"
                tooltipPlacement="bottom"
                tooltipHoverDelayMs={kTooltipHoverDelayMs}
                disableTooltipInteractive
                disableTooltipFocusListener
              />
            </DropdownMenu.Trigger>
            <MenuContent className="min-w-[186px]" sideOffset={5} align="end">
              <MenuItemGroup>
                {isDownloadSupported && <MenuItem label="Download" icon={FileDownload} onSelect={() => void handleDownload()} variant="neutral" />}
                {showStandaloneViewerButton && <MenuItem label="Open in new tab" icon={OpenNew} onSelect={openInNewTab} variant="neutral" />}
              </MenuItemGroup>
            </MenuContent>
          </DropdownMenu.Root>
        )}
      </div>
    )
  }

  return (
    <>
      <div
        className={clsx(
          'grid shrink-0 grid-cols-[26px_auto_84px] items-center px-3 border-b box-content sticky top-0 bg-surface-content',
          badge || isAgencySource ? 'min-h-[60px]' : 'h-[60px]'
        )}
      >
        <div className="w-full text-center flex items-center justify-center">
          <button
            type="button"
            className="text-text-tertiary bg-surface-subtle border border-border-default rounded-[4px] focus:outline-hidden hocus:bg-surface-hover-strong"
            onClick={handleClose}
            aria-label="Close content viewer"
          >
            <span className="sr-only">Close sidebar</span>
            <ContractAlignRight className="h-[14px] w-[14px] m-1" aria-hidden="true" />
          </button>
        </div>
        {badge ? (
          <div className="ml-4 flex flex-col gap-0.5 min-w-0 py-3.5">
            <ContentViewerHeaderTitle title={title} className="pr-1" />
            <div className="self-start">{badge}</div>
          </div>
        ) : (
          <div className="ml-4 min-w-0 pr-1">
            {isAgencySource ? (
              <div className="py-3 text-base font-bold leading-6 compatible-word-break">{title}</div>
            ) : (
              <ContentViewerHeaderTitle title={title} />
            )}
          </div>
        )}
        <div className="flex items-center justify-end gap-1 h-10">
          {isAgencySource && (isDownloadSupported || showStandaloneViewerButton) && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <IconButton variant="plain" color="neutral" size="md" icon={<OverflowMenu />} loading={downloadLoading} aria-label="More actions" />
              </DropdownMenu.Trigger>
              <MenuContent className="min-w-[186px]" sideOffset={5} align="end">
                <MenuItemGroup>
                  {isDownloadSupported && <MenuItem label="Download" icon={FileDownload} onSelect={() => void handleDownload()} variant="neutral" />}
                  {showStandaloneViewerButton && <MenuItem label="Open in new tab" icon={OpenNew} onSelect={openInNewTab} variant="neutral" />}
                </MenuItemGroup>
              </MenuContent>
            </DropdownMenu.Root>
          )}
          {!isAgencySource && (
            <>
              {downloadLoading && <CircularProgressContinuousSized size={16} thickness={7} />}
              {!downloadLoading && isDownloadSupported && (
                <button
                  type="button"
                  className="text-text-tertiary rounded-full focus:outline-hidden hocus:bg-surface-hover-strong h-8 w-8 flex items-center justify-center"
                  aria-label="Download"
                  onClick={handleDownload}
                >
                  <FileDownload className="h-6 w-6" />
                </button>
              )}

              {showStandaloneViewerButton && (
                <Link
                  to={standaloneViewerUrl}
                  target="_blank"
                  className="text-text-tertiary rounded-full focus:outline-hidden hocus:bg-surface-hover-strong h-8 w-8 flex items-center justify-center"
                  aria-label="Open in new tab"
                  onClick={handleOpenInNewTab}
                >
                  <OpenNew className="h-6 w-6" />
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

function buildAnalyticsReferenceFromSession(session: ContentViewerSession): AnalyticsReference | null {
  switch (session.context.kind) {
    case ViewerOpenContextKind.CHAT_CITATION:
      return session.context.analyticsReference ?? null
    default:
      return null
  }
}
